import { inject, Injectable } from '@angular/core'
import { Observable, map } from 'rxjs'
import { LoginResponse, ParticipantService, SessionService } from 'superlikers-microsites-api'

import { userAdapter } from '@core/data/adapters'
import { User, Participant } from '@core/data/models'

@Injectable({
  providedIn: 'root'
})
export class AuthApi {
  #sessionService = inject(SessionService)
  #participantService = inject(ParticipantService)

  /**
   * Fetch current authenticated user.
   * Used to establish or restore session.
   * @returns Observable<User | null> - Null if not authenticated
   */
  fetchCurrentUser(): Observable<User | null> {
    return this.#participantService.getParticipant<Participant>().pipe(
      map(response => {
        if (!response.participation.authenticated) return null
        return userAdapter(response.participation)
      })
    )
  }

  /**
   * Perform login with given credentials.
   * @param data - Login form data (e.g., { email, password })
   * @returns Observable<LoginResponse>
   */
  login(data: Record<string, string>): Observable<LoginResponse> {
    return this.#sessionService.login(data)
  }

  /**
   * Logout current user.
   * Clears session on backend.
   * @returns Observable<void>
   */
  logout() {
    return this.#sessionService.logout()
  }

  /**
   * Check session requirements.
   * It should be called after login frequently.
   * @returns Observable<void>
   */
  checkRequirements() {
    return this.#sessionService.checkRequirements()
  }
}
